const { validateWsUrl } = require('../../security');
const { captureError } = require('../../sentry');

const HANDSHAKE_CLIENT_TYPE = 'sdk';

/**
 * Transport core shared by all SDK WebSocket clients.
 * Owns: URL validation, connect, handshake, listener wiring, JSON parse,
 * stale-socket guard, close, reconnect (opt-in), and Sentry capture.
 * Domain behaviour is supplied via the onMessage / lifecycle callbacks.
 */
class BaseSocketService {
  constructor(options = {}) {
    const {
      oneShot = false,
      reconnect = false,
      onMessage = null,
      onOpen = null,
      onClose = null,
      onError = null,
      errorContext = 'websocket',
    } = options;

    this.oneShot = oneShot;
    this.reconnectConfig = reconnect; // false | { maxAttempts, baseDelayMs, maxDelayMs }
    this.onMessage = onMessage;
    this.onOpen = onOpen;
    this.onClose = onClose;
    this.onError = onError;
    this.errorContext = errorContext;

    this.socket = null;
    this.isConnected = false;
    this._intentionalClose = false;
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._lastConnectParams = null;
  }

  /**
   * Open the socket and send the handshake. Resolves on `open`, rejects on
   * invalid URL / transport error.
   */
  connect({ wsUrl, referenceNumber, merchantId }) {
    return new Promise((resolve, reject) => {
      try {
        if (!validateWsUrl(wsUrl)) {
          const error = { status: 'error', message: 'Invalid WS_URL' };
          this._reportError(error);
          reject(error);
          return;
        }

        this._lastConnectParams = { wsUrl, referenceNumber, merchantId };
        this._intentionalClose = false;
        this._replaceSocket();

        const socket = new WebSocket(wsUrl);
        this.socket = socket;

        socket.addEventListener('open', () => {
          if (this.socket !== socket) return;
          this.isConnected = true;
          this._reconnectAttempts = 0;
          this._sendHandshake(referenceNumber, merchantId);
          if (this.onOpen) this.onOpen();
          resolve(true);
        });

        socket.addEventListener('message', (event) => {
          if (this.socket !== socket) return;
          this._handleRawMessage(event);
        });

        socket.addEventListener('error', (event) => {
          if (this.socket !== socket) return;
          this.isConnected = false;
          const wsError = {
            status: 'error',
            message: 'WebSocket connection error',
            originalError: event,
          };
          this._reportError(wsError);
          reject(wsError);
        });

        socket.addEventListener('close', () => {
          if (this.socket !== socket) return;
          this.isConnected = false;
          this.socket = null;
          if (this.onClose) this.onClose();
          this._maybeReconnect();
        });
      } catch (error) {
        const wsError = {
          status: 'error',
          message: 'Failed to create WebSocket connection',
          originalError: error,
        };
        this._reportError(wsError);
        reject(wsError);
      }
    });
  }

  send(obj) {
    if (this.socket && this.isConnected) {
      this.socket.send(JSON.stringify(obj));
    }
  }

  /** Intentional close — fires onClose but never reconnects. */
  close() {
    this._intentionalClose = true;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
    }
    this.isConnected = false;
  }

  getSocket() {
    return this.socket;
  }

  _sendHandshake(referenceNumber, merchantId) {
    this.send({
      client_type: HANDSHAKE_CLIENT_TYPE,
      reference_number: referenceNumber,
      merchant_id: merchantId,
    });
  }

  _handleRawMessage(event) {
    if (!event || !event.data || event.data === 'pong') return;
    let parsed;
    try {
      parsed = JSON.parse(event.data);
    } catch (error) {
      this._reportError(error);
      return;
    }
    try {
      if (this.onMessage) this.onMessage(parsed);
    } catch (err) {
      this._reportError(err);
    } finally {
      if (this.oneShot) this.close();
    }
  }

  /** Close an existing socket without firing our onClose / reconnect. */
  _replaceSocket() {
    if (this.socket) {
      const old = this.socket;
      this.socket = null; // identity guard makes old.close() a no-op for us
      old.close();
    }
  }

  _maybeReconnect() {
    if (this._intentionalClose) return;
    if (!this.reconnectConfig || !this._lastConnectParams) return;

    const { maxAttempts, baseDelayMs, maxDelayMs } = this.reconnectConfig;
    if (this._reconnectAttempts >= maxAttempts) return;

    const delay = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, this._reconnectAttempts));
    this._reconnectAttempts += 1;
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this.connect(this._lastConnectParams).catch(() => {});
    }, delay);
  }

  _reportError(error) {
    const realError =
      error instanceof Error
        ? error
        : new Error((error && error.message) || 'WebSocket error');
    captureError(realError, { tags: { websocket_context: this.errorContext } });
    if (this.onError) this.onError(error);
  }
}

module.exports = BaseSocketService;
